import { posDiscountOn } from './pos-discount'
import { t, type Key } from './i18n'

/**
 * „მარტივი რეჟიმი" (the owner's plan, item 3, 2026-09-28): the screens show
 * what the shop uses and hide what it does not.
 *
 * Each feature is on or off by the OWNER's choice when he made one (settings
 * KV `feature.<key>`; the discount keeps its own `posDiscount`), and until
 * then by the shop's own behaviour — the rule the till's discount field
 * already followed (lib/pos-discount.ts). A shop that sells on credit keeps
 * ნისია; a shop that never did does not see it. The rule is always stated
 * back to the owner (`autoReason`), so nothing is hidden without a reason he
 * can read, and one switch brings it back.
 *
 * Usage in the shops on 2026-09-28 (5 active, no names): ნისია 2, loyalty 0,
 * a held cart 1, promotions 1, sizes 1, დაშლა 0, outgoing waybills 1.
 */

export type FeatureKey = 'credit' | 'hold' | 'discount' | 'models' | 'conversions' | 'promotions' | 'rsge' | 'repairs' | 'store' | 'askQty' | 'changeTetri' | 'prepaid' | 'fitment' | 'club' | 'tables' | 'tableQr'

export type FeatureMeta = {
  key: FeatureKey
  group: 'pos' | 'warehouse' | 'accounting'
  label: Key
  /** What appears or disappears. */
  shows: Key
  /** 1.69 — a switch that means nothing without another (the table QR without the tables): off while that one is off.
   *  Only a feature that is off for every shop until the owner turns it on may be needed (its pref alone decides). */
  needs?: FeatureKey
}

export const FEATURES: FeatureMeta[] = [
  { key: 'credit', group: 'pos', label: 'feat.credit', shows: 'feat.credit.shows' },
  { key: 'hold', group: 'pos', label: 'feat.hold', shows: 'feat.hold.shows' },
  { key: 'discount', group: 'pos', label: 'feat.discount', shows: 'feat.discount.shows' },
  { key: 'models', group: 'warehouse', label: 'feat.models', shows: 'feat.models.shows' },
  { key: 'conversions', group: 'warehouse', label: 'feat.conversions', shows: 'feat.conversions.shows' },
  { key: 'promotions', group: 'warehouse', label: 'feat.promotions', shows: 'feat.promotions.shows' },
  { key: 'rsge', group: 'accounting', label: 'feat.rsge', shows: 'feat.rsge.shows' },
  // 130 — off until the owner says the shop takes things in for repair
  { key: 'repairs', group: 'pos', label: 'feat.repairs', shows: 'feat.repairs.shows' },
  // B2 — the orders from the shop's website; 153: on by itself where the shop has a store
  { key: 'store', group: 'pos', label: 'feat.store', shows: 'feat.store.shows' },
  // 1.40 — a click on a piece product asks „რამდენი?" too (eggs by the ten, a tray of 30);
  // a weighed product always asks. On by itself in a chicken shop (the owner, 2026-10-14).
  { key: 'askQty', group: 'pos', label: 'feat.askQty', shows: 'feat.askQty.shows' },
  // 1.53 (168) — the change rounding under „ხურდა". Off for every shop until
  // the owner turns it on: „თუ მაღაზიას არ სჭირდება … ასე დარჩეს როგორც ცოცხალზე".
  { key: 'changeTetri', group: 'pos', label: 'feat.changeTetri', shows: 'feat.changeTetri.shows' },
  // 1.57 (174–176) — a gift card and a deposit. Off for every shop: a till still on 1.56
  // cannot read a sale paid from one, so the owner turns it on once every till shows 1.57.
  { key: 'prepaid', group: 'pos', label: 'feat.prepaid', shows: 'feat.prepaid.shows' },
  // 1.62 (187) — „ერგება" (a car or a phone) and OEM numbers on the product card and in Excel. The till finds a
  // product by them whatever the switch: it decides only what the owner is shown.
  { key: 'fitment', group: 'warehouse', label: 'feat.fitment', shows: 'feat.fitment.shows' },
  // 1.63 (188–190) — the loyalty club: the poster, the card at the till, club prices. Off for every shop: a till
  // still on 1.62 cannot read a card, so the owner turns it on once every till shows 1.63 (the 1.57 rule).
  { key: 'club', group: 'pos', label: 'feat.club', shows: 'feat.club.shows' },
  // 1.66 (192) — a café's tables and open bills. Off for every shop, a café too: a till still on 1.65 cannot show
  // a bill, so the owner turns it on once every till shows 1.66 (the 1.57 rule).
  { key: 'tables', group: 'pos', label: 'feat.tables', shows: 'feat.tables.shows' },
  // 1.69 (197/198) — the guests' table QR: call the waiter, ask for the bill, order from the menu. Off for every shop: a
  // till or a waiter's phone still on 1.68 cannot see a guest's request, so the owner turns it on once every device
  // shows 1.69 (the 1.57 rule). Meaningful only with the tables on.
  { key: 'tableQr', group: 'pos', label: 'feat.tableQr', shows: 'feat.tableQr.shows', needs: 'tables' },
]

/** 1.69 — the switch a feature needs is on (the owner's choice: a needed feature is off for every shop by default). */
export function featureNeedMet(key: FeatureKey, prefs: Partial<Record<FeatureKey, boolean>> | undefined): boolean {
  const need = FEATURES.find((f) => f.key === key)?.needs
  return !need || prefs?.[need] === true
}

export const FEATURE_KEYS = FEATURES.map((f) => f.key)

/** The owner's switches as the settings rows hold them: `feature.<key>` = 'true' / 'false' is a
 *  choice, anything else (an empty value) is automatic. One rule for the start and, since 1.57,
 *  for the live read when another device changes one (Codex, final review #2). */
export function featuresFromKv(kv: Record<string, string | undefined>): Partial<Record<FeatureKey, boolean>> {
  return Object.fromEntries(FEATURE_KEYS.flatMap((k): [FeatureKey, boolean][] => {
    const v = kv[`feature.${k}`]
    return v === 'true' || v === 'false' ? [[k, v === 'true']] : []
  })) as Partial<Record<FeatureKey, boolean>>
}

/** A feature's automatic answer is trustworthy only after boot history lands. */
export const featureLive = (s: { isHydrated: boolean; isOnline: boolean | undefined; historyReady: boolean }) =>
  s.isHydrated && s.isOnline !== false && s.historyReady

/** The slice of the store the automatic rules read. Structural — the whole
 *  store satisfies it and a test hands over a few fields. */
export type UsageState = {
  customers: { id: string }[]
  sales: { payment_method: string; discount?: number | null }[]
  cashiers: { active?: boolean; max_discount_pct?: number | null }[]
  products: { model_id?: string | null; fits?: unknown; oem_codes?: unknown }[]
  productConversions: unknown[]
  /** 1.55 — one → many breakdowns (172). */
  productBreakdowns: unknown[]
  promotions: unknown[]
  shopType: string
  /** 153 — the shop has a website (a store_sites row, which only the platform admin makes). */
  hasStore?: boolean
}

type Auto = { on: boolean; why: string }

const CONVERSION_TEMPLATES = ['produce', 'poultry', 'kiosk', 'petshop', 'household']

/** What the shop's own behaviour says, and why — in words for the card. */
export function autoFeature(key: FeatureKey, s: UsageState): Auto {
  switch (key) {
    case 'credit': {
      const debt = s.sales.some((x) => x.payment_method === 'credit')
      if (s.customers.length > 0) return { on: true, why: t('feat.why.customers', { n: s.customers.length }) }
      if (debt) return { on: true, why: t('feat.why.creditSales') }
      // 1.55 — a neighbourhood grocery's first tip is ნისია: its button is there from day one
      if (s.shopType === 'grocery') return { on: true, why: t('feat.why.grocery') }
      return { on: false, why: t('feat.why.noCredit') }
    }
    case 'hold':
      // A held cart leaves no trace once it is sold, so there is nothing to
      // read the habit from: shown until the owner says otherwise.
      return { on: true, why: t('feat.why.holdDefault') }
    case 'discount': {
      const on = posDiscountOn(null, s.cashiers, s.sales.some((x) => (x.discount ?? 0) > 0))
      return on
        ? { on: true, why: t('feat.why.discountUsed') }
        : { on: false, why: t('feat.why.noDiscount') }
    }
    case 'models': {
      if (s.products.some((p) => p.model_id)) return { on: true, why: t('feat.why.hasModels') }
      if (s.shopType === 'clothing') return { on: true, why: t('feat.why.clothing') }
      return { on: false, why: t('feat.why.noModels') }
    }
    case 'conversions': {
      if (s.productConversions.length > 0 || s.productBreakdowns.length > 0) return { on: true, why: t('feat.why.convUsed') }
      if (s.shopType === 'coffee') return { on: true, why: t('feat.why.coffee') }
      // 1.55 — the templates whose tips name „დაშლა" (a carcass, eggs, a block of cigarettes,
      // a sack of feed, a set of plates) find the button on a new shop
      if (CONVERSION_TEMPLATES.includes(s.shopType)) return { on: true, why: t('feat.why.convTemplate') }
      return { on: false, why: t('feat.why.noConv') }
    }
    case 'promotions':
      return s.promotions.length > 0
        ? { on: true, why: t('feat.why.hasPromo') }
        : { on: false, why: t('feat.why.noPromo') }
    case 'rsge':
      return { on: true, why: t('feat.why.rsgeDefault') }
    case 'repairs':
      return { on: false, why: t('feat.why.repairsOff') }
    case 'askQty':
      return s.shopType === 'poultry'
        ? { on: true, why: t('feat.why.poultry') }
        : { on: false, why: t('feat.why.askQtyOff') }
    case 'changeTetri':
      return { on: false, why: t('feat.why.changeTetriOff') }
    case 'prepaid':
      return { on: false, why: t('feat.why.prepaidOff') }
    case 'club':
      return { on: false, why: t('feat.why.clubOff') }
    case 'tables':
      return { on: false, why: t('feat.why.tablesOff') }
    case 'tableQr':
      return { on: false, why: t('feat.why.tableQrOff') }
    case 'fitment':
      if (s.products.some((p) => (Array.isArray(p.fits) && p.fits.length > 0) || (Array.isArray(p.oem_codes) && p.oem_codes.length > 0))) {
        return { on: true, why: t('feat.why.fitmentData') }
      }
      if (s.shopType === 'autoparts' || s.shopType === 'accessories') return { on: true, why: t('feat.why.fitmentTemplate') }
      return { on: false, why: t('feat.why.fitmentOff') }
    case 'store':
      return s.hasStore
        ? { on: true, why: t('feat.why.storeOn') }
        : { on: false, why: t('feat.why.storeOff') }
  }
}

/**
 * 153 (Codex's review) — where the device keeps the automatic answers: per
 * user and shop, so a shared device never shows one shop's modules in
 * another's till. No identity yet → nothing is kept or read.
 */
export const FEATURE_CACHE_PREFIX = 'feature-auto'
/**
 * Strict audit 2 (L2) — sign-out removes every shop's cached answers on this
 * device (`feature-auto:<user>:<org>`), not only a bare prefix that is never a
 * key. Returns how many it removed.
 */
export function clearFeatureCache(storage: Pick<Storage, 'length' | 'key' | 'removeItem'>): number {
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (k && k.startsWith(`${FEATURE_CACHE_PREFIX}:`)) doomed.push(k)
  }
  for (const k of doomed) storage.removeItem(k)
  return doomed.length
}

export function featureCacheKey(userId?: string | null, orgId?: string | null): string | null {
  return userId && orgId ? `${FEATURE_CACHE_PREFIX}:${userId}:${orgId}` : null
}

/**
 * The modules that ARE a feature (Codex, 153): while it is off they are in no
 * menu — the desktop sidebar and the phone's „მეტი" ask the same question.
 */
export const FEATURE_ROUTES: Partial<Record<string, FeatureKey>> = { '/credit': 'credit', '/repairs': 'repairs', '/orders': 'store' }

export function routeFeatureOk(href: string, on: (key: FeatureKey) => boolean): boolean {
  const key = FEATURE_ROUTES[href]
  return key === undefined || on(key)
}

/** On or off: the owner's choice when he made one, else the shop's behaviour. */
export function featureOn(pref: boolean | null | undefined, auto: Auto): boolean {
  return pref === true || pref === false ? pref : auto.on
}
